/**
 * The three §6.3 governance tabs (§10.3).
 *
 * They were placeholders until the gaps screen filled 3002 and eight board
 * reports arrived. Every name, role and figure here is rendered exactly as
 * extracted (§11) -- a name that was misread is shown misread, because a
 * screen that quietly tidied it would hide the one thing worth finding.
 */
import { useQuery } from '@tanstack/react-query'
import { Link } from 'wouter'
import { useState } from 'react'
import type {
  GovernanceResponse, RemunerationResponse, Subsidiary,
} from '@server/sql/governance.ts'
import { ApiError, api, keys } from '~/lib/api.ts'
import { useT } from '~/i18n/index.tsx'
import { count, percent0 } from '~/i18n/format.ts'
import { Badge, Label, Skeleton } from '~/components/ui/primitives.tsx'
import { EmptyState, ErrorState } from '~/components/ui/empty.tsx'
import { Table, Td, Th, Tr } from '~/components/ui/table.tsx'
import { Disclosure } from '~/components/ui/disclosure.tsx'
import { Raw, Untranslated } from '~/components/ui/raw.tsx'
import { Segmented } from '~/components/ui/tabs.tsx'
import { cn } from '~/lib/cn.ts'

/** Shared shell: skeleton, error, and a why-is-this-empty message. */
function Panel<T>({ query, empty, children }: {
  query: { data: T | undefined; error: unknown; refetch: () => void }
  empty: string
  children: (data: T) => React.ReactNode
}) {
  const { t } = useT()
  if (query.error) {
    const e = query.error as ApiError
    return <ErrorState title={t('common.error')} detail={e.message} requestId={e.requestId}
      onRetry={() => query.refetch()} retryLabel={t('action.retry')} />
  }
  if (!query.data) return <Skeleton className="h-72 w-full" />
  return <>{children(query.data)}</>
}

// ---------------------------------------------------------------------
// Governance
// ---------------------------------------------------------------------

export function CompanyGovernance({ symbol }: { symbol: string }) {
  const { t, tn, locale, v } = useT()
  const q = useQuery({
    queryKey: keys.companyGovernance(symbol),
    queryFn: () => api<GovernanceResponse>(`/api/companies/${encodeURIComponent(symbol)}/governance`),
  })

  return (
    <Panel query={q} empty={t('empty.governanceNow')}>
      {(data) => {
        if (data.people.length === 0)
          return <EmptyState title={t('empty.generic')}>{t('empty.governanceNow')}</EmptyState>
        const n = (x: number) => count(x, locale)
        return (
          <div className="space-y-3">
            <div className="flex flex-wrap items-baseline gap-x-6 gap-y-2">
              <Figure label={t('gov.people')} value={n(data.totals.people)} />
              <Figure label={t('gov.memberships')} value={n(data.totals.memberships)} />
            </div>

            {/* The misreadings, named rather than filtered away. */}
            {data.totals.flagged > 0 && (
              <p className="max-w-[76ch] rounded-[var(--radius-control)] border border-[var(--warn)]/30 bg-[var(--warn-bg)] px-2.5 py-1.5 text-[length:var(--text-data)] leading-[var(--text-data--line-height)] text-[var(--text)]">
                <span className="font-medium">{tn('gov.flagged', data.totals.flagged, { n: n(data.totals.flagged) })}. </span>
                {t('gov.flaggedWhy')}
              </p>
            )}

            <Table>
              <thead>
                <Tr>
                  <Th>{t('gov.name')}</Th>
                  <Th>{t('gov.body')}</Th>
                  <Th>{t('gov.classification')}</Th>
                  <Th>{t('gov.years')}</Th>
                </Tr>
              </thead>
              <tbody>
                {data.people.map((p) => {
                  const latest = p.memberships[0]
                  return (
                    <Tr key={p.personId} className={cn(p.looksUnnamed && 'opacity-60')}>
                      <Td>
                        {p.looksUnnamed ? (
                          <span className="flex flex-wrap items-baseline gap-1.5">
                            <Untranslated>{p.nameRaw ?? ''}</Untranslated>
                            <Badge state="warn">{t('gov.unnamed')}</Badge>
                          </span>
                        ) : (
                          <Raw lang={p.lang ?? undefined}>{p.nameRaw ?? ''}</Raw>
                        )}
                      </Td>
                      <Td className="text-[var(--text-muted)]">
                        {p.bodies.map((b) => v('body', b) ?? b).join(' · ')}
                      </Td>
                      <Td className="text-[var(--text-muted)]">
                        {latest?.classification
                          ? <Raw lang={p.lang ?? undefined}>{latest.classification}</Raw>
                          : <span className="text-[var(--text-faint)]">—</span>}
                      </Td>
                      <Td numeric className="tnum text-[var(--text-muted)]">
                        {p.years.length ? p.years.join(', ') : '—'}
                      </Td>
                    </Tr>
                  )
                })}
              </tbody>
            </Table>
          </div>
        )
      }}
    </Panel>
  )
}

// ---------------------------------------------------------------------
// Remuneration
// ---------------------------------------------------------------------

export function CompanyRemuneration({ symbol }: { symbol: string }) {
  const { t, locale } = useT()
  const [year, setYear] = useState<number | null>(null)
  const q = useQuery({
    queryKey: keys.companyRemuneration(symbol, year),
    queryFn: () =>
      api<RemunerationResponse>(
        `/api/companies/${encodeURIComponent(symbol)}/remuneration` +
        (year === null ? '' : `?year=${year}`),
      ),
  })

  return (
    <Panel query={q} empty={t('empty.remunerationNow')}>
      {(data) => {
        if (data.rows.length === 0 && year === null)
          return <EmptyState title={t('empty.generic')}>{t('empty.remunerationNow')}</EmptyState>
        return (
          <div className="space-y-3">
            {data.years.length > 1 && (
              <Segmented
                size="sm"
                ariaLabel={t('rem.year')}
                options={[
                  { id: 'all', label: t('rem.allYears') },
                  ...data.years.map((y) => ({ id: String(y), label: String(y) })),
                ]}
                value={year === null ? 'all' : String(year)}
                onChange={(id) => setYear(id === 'all' ? null : Number(id))}
              />
            )}

            <p className="max-w-[76ch] text-[length:var(--text-data)] leading-[var(--text-data--line-height)] text-[var(--text-muted)]">
              {t('rem.typedNote')}
            </p>

            <div className="space-y-1">
              {data.rows.map((r) => (
                <Disclosure
                  key={r.id}
                  className="rounded-[var(--radius-container)] border border-[var(--border)] bg-[var(--bg-raised)] px-3 py-1.5"
                  summary={
                    <span className="flex min-w-0 flex-1 flex-wrap items-baseline gap-x-2 gap-y-0.5">
                      <Raw className="text-[length:var(--text-data)] text-[var(--text)]">
                        {r.nameRaw ?? '—'}
                      </Raw>
                      {r.fiscalYear !== null && (
                        <span className="tnum text-[length:var(--text-micro)] text-[var(--text-faint)]">
                          {r.fiscalYear}
                        </span>
                      )}
                      <span className="flex-1" />
                      {/* The typed value: computed, so it goes through Intl. */}
                      <span className="tnum text-[length:var(--text-data)] font-medium text-[var(--text)]">
                        {r.total === null ? '—' : count(Number(r.total), locale)}
                      </span>
                      <span className="text-[length:var(--text-micro)] text-[var(--text-faint)]">
                        {r.currency}
                      </span>
                    </span>
                  }
                >
                  <RawCells valueRaw={r.valueRaw} documentId={r.documentId} pageNo={r.pageNo} />
                </Disclosure>
              ))}
            </div>
          </div>
        )
      }}
    </Panel>
  )
}

/**
 * The printed cells, under the headings the page used.
 *
 * Never through `Intl`: these are the strings the filing carried, and a
 * figure that has been reformatted cannot be compared against the page it
 * was read from -- which is the whole reason this app exists (§11).
 */
function RawCells({ valueRaw, documentId, pageNo }: {
  valueRaw: Record<string, unknown> | null
  documentId: number
  pageNo: number | null
}) {
  const { t } = useT()
  const entries = Object.entries(valueRaw ?? {})
  if (entries.length === 0) return null

  return (
    <div className="pb-2 pt-1">
      <div className="mb-1 flex items-baseline gap-2">
        <Label>{t('rem.asPrinted')}</Label>
        <Link
          href={pageNo === null ? `/d/${documentId}` : `/d/${documentId}?p=${pageNo}`}
          className="link-inline text-[length:var(--text-micro)] text-[var(--accent)]"
        >
          d{documentId}{pageNo !== null && ` p${pageNo}`}
        </Link>
      </div>
      <div className="grid gap-x-4 gap-y-0.5 sm:grid-cols-2 lg:grid-cols-3">
        {entries.map(([heading, cell]) => {
          const raw = (cell as { number?: { raw?: string; is_dash?: boolean } })?.number
          return (
            <div key={heading} className="flex items-baseline justify-between gap-2 border-b border-[var(--border)] py-0.5">
              <Raw lang="ar" className="min-w-0 flex-1 truncate text-[length:var(--text-micro)] text-[var(--text-muted)]" title={heading}>
                {heading}
              </Raw>
              <span className="tnum shrink-0 text-[length:var(--text-tiny)] text-[var(--text)]">
                {raw?.is_dash
                  ? <span className="text-[var(--text-faint)]">—</span>
                  : <Raw>{raw?.raw ?? ''}</Raw>}
              </span>
            </div>
          )
        })}
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------
// Subsidiaries
// ---------------------------------------------------------------------

export function CompanySubsidiaries({ symbol }: { symbol: string }) {
  const { t, locale } = useT()
  const q = useQuery({
    queryKey: keys.companySubsidiaries(symbol),
    queryFn: () =>
      api<{ rows: Subsidiary[] }>(`/api/companies/${encodeURIComponent(symbol)}/subsidiaries`),
  })

  return (
    <Panel query={q} empty={t('empty.subsidiariesNow')}>
      {(data) => {
        if (data.rows.length === 0)
          return <EmptyState title={t('empty.generic')}>{t('empty.subsidiariesNow')}</EmptyState>
        return (
          <Table>
            <thead>
              <Tr>
                <Th>{t('sub.name')}</Th>
                <Th numeric>{t('sub.capital')}</Th>
                <Th numeric>{t('sub.ownership')}</Th>
                <Th>{t('sub.activity')}</Th>
                <Th>{t('sub.operation')}</Th>
                <Th>{t('sub.incorporation')}</Th>
              </Tr>
            </thead>
            <tbody>
              {data.rows.map((r) => (
                <Tr key={r.id}>
                  <Td><Raw>{r.nameRaw ?? '—'}</Raw></Td>
                  <Td numeric className="tnum text-[var(--text-muted)]">
                    {r.capital === null ? '—' : `${count(Number(r.capital), locale)} ${r.currency ?? ''}`}
                  </Td>
                  <Td numeric className="tnum text-[var(--text-muted)]">
                    {r.ownershipPct === null ? '—' : percent0(r.ownershipPct / 100, locale)}
                  </Td>
                  <Td className="text-[var(--text-muted)]">
                    <Raw>{r.mainActivityRaw ?? '—'}</Raw>
                  </Td>
                  <Td className="text-[var(--text-muted)]"><Raw>{r.countryOperation ?? '—'}</Raw></Td>
                  <Td className="text-[var(--text-muted)]"><Raw>{r.countryIncorporation ?? '—'}</Raw></Td>
                </Tr>
              ))}
            </tbody>
          </Table>
        )
      }}
    </Panel>
  )
}

function Figure({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <Label>{label}</Label>
      <div className="tnum mt-0.5 text-[length:var(--text-title)] font-medium text-[var(--text)]">
        {value}
      </div>
    </div>
  )
}
